// src/components/onboarding/stage5-scoring.tsx
//
// Stage 5 — Response scoring (admin-configurable, self-managed).
//
// Owns its scorer-model picker, rubric version, concurrency controls, run
// button, and the Stage 4 / Stage 5 log exports. renderActionButton() in
// onboarding-page.tsx returns null for Stage 5.
//
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, AlertTriangle, Check, Gauge, FileText,
  ShieldCheck, ChevronRight, Download, Database, StopCircle,
  Lock, Scale, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import type { CustomerDetail } from "@/api/onboarding-api";
import { downloadRunResponses } from "@/api/onboarding-api";
import {
  listScorerModels,
  getScoringState,
  startScoringRun,
  getActiveScoringRun,
  cancelScoringRun,
  openScoringRunWs,
  downloadScoringExport,
  downloadCleanroomExport,
  runPredeploymentValidation,
  getPredeploymentValidation,
  activateScoringPass,
  measuredScoringCost,
  type ScorerModel,
  type ScoringMode,
  type ScoringState,
  type ScoringSummary,
  type ScoringCounts,
  type ScoringPass,
  type ScoringRun,
  type ScoringRunWsMessage,
  type PredeploymentValidation,
} from "@/api/onboarding-scoring-api";
import { useResumableWs } from "./hooks/use-resumable-ws";
import { RubricResults } from "./rubric-results";

interface Props {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
  onOpenReview?: (runId: number) => void;
}

export function Stage5Scoring({
  customer,
  onCustomerUpdate,
  onStageComplete,
  onOpenReview,
}: Props) {
  const runId = customer.measurement_run_id ?? null;

  const [models, setModels] = useState<ScorerModel[]>([]);
  const [state, setState] = useState<ScoringState | null>(null);
  const [scorerId, setScorerId] = useState<string>("");
  const [rubricVersion, setRubricVersion] = useState<string>("");
  const [concurrency, setConcurrency] = useState<number>(5);

  // True while `scorerId` holds nothing but a pre-selection. The judge this run
  // was configured with at Stage 4 may replace a pre-selection — never a judge
  // the operator picked, nor one restored from their last visit to this run.
  //
  // Without this the picker opened on the REGISTRY DEFAULT and submitted it, so
  // on a run pinned to an older judge (customer 8 / run 37, claude-opus-4-7)
  // every operator who just pressed the button asked the server about
  // claude-opus-4-8 — a judge that run was never scored with — and got the
  // second-pass 409 instead of the free adopt.
  const scorerIsPreselectionRef = useRef(true);

  const [scoringRun, setScoringRun] = useState<ScoringRun | null>(null);
  const [enqueueing, setEnqueueing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [summary, setSummary] = useState<ScoringSummary | null>(
    (customer.scoring_summary as ScoringSummary) &&
      Object.keys(customer.scoring_summary as object).length > 0
      ? (customer.scoring_summary as ScoringSummary)
      : null,
  );

  // Cleanroom spec, Change 4: once a completed stage5 exists, the Run Scoring
  // button is disabled by default to prevent silent re-runs. An admin must
  // explicitly opt in to a fresh scoring attempt.
  const [allowReScore, setAllowReScore] = useState(false);
  // Undefined = let the server decide, which is the batch API at half price.
  // Set to "direct" only when an operator needs the result today; the control
  // that sets it says what that costs.
  const [scoringMode, setScoringMode] = useState<ScoringMode | undefined>(undefined);
  // The re-judge form is collapsed by default once a pass exists. Opening it is
  // the deliberate act; it is the only route to a second paid pass.
  const [showRejudge, setShowRejudge] = useState(false);
  const [activating, setActivating] = useState<string | null>(null);

  // Pre-deployment two-arm validation state.
  const [predeploy, setPredeploy] = useState<PredeploymentValidation | null>(null);
  const [predeployBusy, setPredeployBusy] = useState(false);

  // Live progress mirrored out of the WS event stream so the bar
  // updates between snapshot refreshes.
  const [liveCompleted, setLiveCompleted] = useState(0);
  const [liveTotal, setLiveTotal] = useState(0);
  const [livePct, setLivePct] = useState(0);
  const [liveCost, setLiveCost] = useState<number | null>(0);
  // Set when the judge batch outlives one worker pass's wait budget. Scoring is
  // in progress and the batch is not resubmitted — this exists so a slow batch
  // reads as "waiting on the provider" rather than as a frozen bar.
  const [awaitingBatch, setAwaitingBatch] = useState<
    { batchId: string; finished: number; total: number } | null
  >(null);

  const isRunning =
    !!scoringRun &&
    (scoringRun.status === "pending" || scoringRun.status === "running");

  useEffect(() => {
    listScorerModels()
      .then((r) => {
        setModels(r.models);
        const def = r.models.find((m) => m.is_default) ?? r.models[0];
        if (def) setScorerId((prev) => prev || def.id);
      })
      .catch((e) => toast.error(e?.message || "Failed to load scorer models"));
  }, []);

  const refreshState = useCallback(async () => {
    if (runId == null) return null;
    try {
      const s = await getScoringState(customer.id, runId);
      setState(s);
      setRubricVersion((prev) => prev || s.suggested_rubric_version);
      // This run's own judge outranks the registry default, which is only a
      // fallback for runs configured before the judge was pinned at Stage 4.
      const runJudge = s.configured_scorer_model_id;
      if (runJudge && scorerIsPreselectionRef.current) {
        scorerIsPreselectionRef.current = false;
        setScorerId(runJudge);
      } else {
        setScorerId((prev) => prev || s.default_scorer_model);
      }
      // The server is the authority on which pass the reports read, so the
      // displayed result follows it rather than whatever this tab last saw.
      if (s.active_summary) setSummary(s.active_summary);
      return s;
    } catch {
      return null; // run may not be ready yet
    }
  }, [customer.id, runId]);

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  // Keep the state LIVE while a judge pass is running.
  //
  // Stage 5 used to render whatever snapshot the page happened to load with.
  // Opened while the auto-pipeline was mid-pass, it showed "Stored scores 32"
  // and a Run-scoring button for a run that was in fact being scored — one
  // click from a second full pass, and only a manual refresh made it honest
  // (customer 14, run 32). Polling removes the window entirely; it stops as
  // soon as the pass finishes, so an idle Stage 5 makes no requests.
  //
  // The manual re-score path has its own WebSocket and is excluded here so the
  // two never fight over the same state.
  const autoPassRunning = !!state?.scoring_in_flight && !isRunning;
  useEffect(() => {
    if (!autoPassRunning) return;
    const timer = setInterval(() => {
      // refreshState carries the active pass's summary, so the finished result
      // renders on the tick the pass lands — no reload, no stale count.
      void refreshState();
    }, 5000);
    return () => clearInterval(timer);
  }, [autoPassRunning, refreshState]);

  // A tab left open for an hour is the other way to be stale. Re-check on
  // focus, which costs one request and covers every case polling does not.
  useEffect(() => {
    if (runId == null) return;
    const onFocus = () => void refreshState();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshState, runId]);

  // On mount / runId change: snapshot the scoring run state from the
  // server. WS attach/detach is now driven by useResumableWs below off
  // the scoringRun's lifecycle status.
  useEffect(() => {
    if (runId == null) return;
    let cancelled = false;
    getActiveScoringRun(customer.id, runId)
      .then((r) => {
        if (cancelled) return;
        const sr = r.scoring_run;
        setScoringRun(sr);
        if (sr) {
          setLiveCompleted(sr.progress_completed);
          setLiveTotal(sr.progress_total);
          setLivePct(
            sr.progress_total > 0
              ? (sr.progress_completed / sr.progress_total) * 100
              : 0,
          );
          setLiveCost(sr.cost_so_far_usd);
        }
      })
      .catch(() => {
        /* no scoring run yet — that's fine */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, runId]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === scorerId),
    [models, scorerId],
  );

  const constraintNote = useMemo(() => {
    const pc = selectedModel?.param_constraints ?? {};
    const omitted = Object.entries(pc)
      .filter(([, v]) => v === "omit")
      .map(([k]) => k);
    const fixed = Object.entries(pc)
      .filter(([, v]) => v !== "omit")
      .map(([k, v]) => `${k}=${String(v)}`);
    const parts: string[] = [];
    if (omitted.length) parts.push(`${omitted.join(", ")} omitted (API rejects it)`);
    if (fixed.length) parts.push(`pinned ${fixed.join(", ")}`);
    return parts.join(" · ");
  }, [selectedModel]);

  // Persist the scorer + concurrency controls per run so they survive a remount
  // (navigating away and back, or the panel unmounting mid-flow). Without this
  // the picker resets to defaults every time the component re-mounts. Keyed by
  // customer + run; hydrated before we start writing so we never clobber the
  // saved value with the initial defaults.
  const controlsKey = runId != null ? `stage5-controls:${customer.id}:${runId}` : null;
  const controlsHydratedRef = useRef(false);
  useEffect(() => {
    controlsHydratedRef.current = false;
    // A different run brings its own configured judge, so the value carried
    // over from the previous run is a pre-selection again.
    scorerIsPreselectionRef.current = true;
    if (!controlsKey) return;
    try {
      const raw = localStorage.getItem(controlsKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.scorerId) {
          scorerIsPreselectionRef.current = false;
          setScorerId(saved.scorerId);
        }
        if (typeof saved?.concurrency === "number") setConcurrency(saved.concurrency);
      }
    } catch { /* ignore malformed / unavailable storage */ }
    controlsHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlsKey]);
  useEffect(() => {
    // Skip until hydrated, and skip the empty pre-selection so we never
    // overwrite a saved scorer with "".
    if (!controlsKey || !controlsHydratedRef.current || !scorerId) return;
    try {
      localStorage.setItem(controlsKey, JSON.stringify({ scorerId, concurrency }));
    } catch { /* ignore */ }
  }, [controlsKey, scorerId, concurrency]);

  useEffect(() => {
    if (runId == null || !summary) return;
    getPredeploymentValidation(customer.id, runId)
      .then((r) => setPredeploy(r.validation))
      .catch(() => { /* none yet */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, runId, summary?.scored_at]);

  const runValidation = async () => {
    if (runId == null) return;
    setPredeployBusy(true);
    try {
      const result = await runPredeploymentValidation(customer.id, runId);
      setPredeploy(result);
      toast.success(`Pre-deployment validation: ${result.verdict}`);
    } catch (e: any) {
      toast.error(e?.message || "Validation failed");
    } finally {
      setPredeployBusy(false);
    }
  };

  const guardedDownload = async (key: string, fn: () => Promise<void>) => {
    setDownloading(key);
    try {
      await fn();
    } catch (e: any) {
      toast.error(e?.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  if (runId == null) {
    return (
      <Card title="Scoring — measurement run required">
        <p style={pMuted}>
          Run the measurement battery (Stage 4) first. Scoring grades every
          stored response with the selected judge model.
        </p>
      </Card>
    );
  }

  const handleWsClose = useCallback(() => {
    if (runId == null) return;
    getActiveScoringRun(customer.id, runId)
      .then((r) => {
        if (!r.scoring_run) return;
        setScoringRun(r.scoring_run);
        if (r.scoring_run.status === "completed" && r.scoring_run.summary) {
          setSummary(r.scoring_run.summary);
          onStageComplete();
        }
      })
      .catch(() => { /* user can refresh */ });
  }, [customer.id, runId, onStageComplete]);

  const handleWsEvent = useCallback((msg: ScoringRunWsMessage) => {
    if (msg.type === "snapshot") {
      setScoringRun(msg.scoring_run);
      setLiveCompleted(msg.scoring_run.progress_completed);
      setLiveTotal(msg.scoring_run.progress_total);
      setLivePct(
        msg.scoring_run.progress_total > 0
          ? (msg.scoring_run.progress_completed / msg.scoring_run.progress_total) * 100
          : 0,
      );
      setLiveCost(msg.scoring_run.cost_so_far_usd);
    } else if (msg.type === "scoring_started") {
      setAwaitingBatch(null);
      setLiveTotal(msg.total);
      setLiveCompleted(0);
      setLivePct(0);
      setLiveCost(0);
    } else if (msg.type === "scoring_progress") {
      setLiveCompleted(msg.completed);
      setLiveTotal(msg.total);
      setLivePct(msg.percent);
      setLiveCost(msg.cost_so_far);
      setScoringRun((prev) =>
        prev ? {
          ...prev,
          status: "running",
          progress_completed: msg.completed,
          progress_total: msg.total,
          cost_so_far_usd: msg.cost_so_far,
        } : prev,
      );
    } else if (msg.type === "scoring_complete") {
      setAwaitingBatch(null);
      setLivePct(100);
      // The measured figure only. Falling back to the flat per-call estimate
      // here would replace a real bill with a judge-independent constant at the
      // exact moment the operator reads "what did this cost".
      const finalCost = measuredScoringCost(msg.summary);
      setLiveCost(finalCost);
      setSummary(msg.summary);
      setScoringRun((prev) =>
        prev ? {
          ...prev,
          status: "completed",
          progress_completed: msg.summary.responses_scored,
          // The scoring_run row's running total stays as the worker reported it
          // when this judge has no measurable price — better a stale tally than
          // a zero that reads as "this pass was free".
          cost_so_far_usd: finalCost ?? prev.cost_so_far_usd,
          summary: msg.summary,
          finished_at: msg.timestamp,
        } : prev,
      );
      if (msg.customer) onCustomerUpdate(msg.customer);
      if (runId != null) {
        getScoringState(customer.id, runId).then(setState).catch(() => { /* skip */ });
      }
      toast.success(
        `Scored ${msg.summary.responses_scored} responses` +
          (msg.summary.failed_zeroed
            ? ` (${msg.summary.failed_zeroed} fell back to zero)`
            : ""),
      );
      onStageComplete();
    } else if (msg.type === "scoring_cancelled") {
      setAwaitingBatch(null);
      setScoringRun((prev) =>
        prev ? {
          ...prev,
          status: "cancelled",
          progress_completed: msg.completed,
          progress_total: msg.total,
          cost_so_far_usd: msg.cost_usd,
          finished_at: msg.timestamp,
        } : prev,
      );
      toast.info("Scoring cancelled — partial scores preserved.");
    } else if (msg.type === "scoring_awaiting_batch") {
      // Not an error and not a stall: the batch is running at the provider and
      // a continuation will collect it. Say so, because the alternative is a
      // progress bar that stops moving with no explanation.
      setAwaitingBatch({
        batchId: msg.provider_batch_id,
        finished: msg.batch_finished,
        total: msg.batch_total,
      });
    } else if (msg.type === "error") {
      setAwaitingBatch(null);
      setError(msg.message);
      setScoringRun((prev) =>
        prev ? { ...prev, status: "failed", error_message: msg.message } : prev,
      );
      toast.error(msg.message || "Scoring failed");
    }
  }, [customer.id, onCustomerUpdate, onStageComplete, runId]);

  // Open a scoring-run WS while the run is pending/running; close on
  // terminal states. The hook tears the socket down on unmount.
  const scoringWsKey = useMemo(() => {
    if (!scoringRun) return null;
    if (scoringRun.status !== "pending" && scoringRun.status !== "running") return null;
    return scoringRun.id;
  }, [scoringRun]);

  useResumableWs<ScoringRunWsMessage>({
    connectionKey: scoringWsKey,
    openWs: () =>
      openScoringRunWs({
        scoringRunId: scoringWsKey as number, // guaranteed non-null when this fires
        onEvent: () => { /* hook overrides */ },
      }),
    onMessage: handleWsEvent,
    onClose: handleWsClose,
  });

  // What Direct costs relative to Batch. Server-supplied so the label can never
  // become a second copy of a number that drifts from the pricing code; the 2
  // is only the value before the first state fetch lands.
  const directCostMultiplier = state?.direct_mode_cost_multiplier ?? 2;

  const run = async () => {
    setError(null);
    setEnqueueing(true);
    try {
      // rubric_version is intentionally omitted: the backend derives it from
      // the approved framework so every score is stamped with a real,
      // traceable version. The field below is display-only.
      // force_rescore mirrors the "allow re-score" opt-in: without it the
      // backend adopts an existing, current scoring pass instead of paying a
      // second judge pass on the same run.
      const res = await startScoringRun(customer.id, runId, {
        scorer_model_id: scorerId || undefined,
        max_concurrency: concurrency,
        force_rescore: allowReScore,
        scoring_mode: scoringMode,
      });
      if (res.adopted && res.scoring_summary) {
        // A complete, current scoring pass already existed — the backend
        // adopted it and advanced the stage WITHOUT re-judging. Reflect the
        // finished state locally; no WS to open.
        setSummary(res.scoring_summary);
        setScoringRun(null);
        setLivePct(100);
        setLiveCost(measuredScoringCost(res.scoring_summary));
        if (res.customer) onCustomerUpdate(res.customer);
        getScoringState(customer.id, runId).then(setState).catch(() => { /* skip */ });
        toast.success("Adopted the existing scoring pass — no re-judge, stage advanced.");
        onStageComplete();
        return;
      }
      if (res.scoring_run) {
        setScoringRun(res.scoring_run);
        setLiveCompleted(0);
        setLiveTotal(res.scoring_run.progress_total);
        setLivePct(0);
        setLiveCost(0);
      }
      // useResumableWs auto-attaches as soon as scoringWsKey reflects
      // the new pending/running run id.
    } catch (e: any) {
      const msg = e?.message || "Failed to start scoring";
      setError(msg);
      toast.error(msg);
    } finally {
      setEnqueueing(false);
    }
  };

  const handleCancel = async () => {
    if (!scoringRun) return;
    setCancelling(true);
    try {
      await cancelScoringRun(scoringRun.id);
      toast.info("Cancellation requested — worker will stop at the next batch.");
      setScoringRun((prev) => prev ? { ...prev, cancellation_requested: true } : prev);
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  if (isRunning) {
    const cancelRequested = scoringRun?.cancellation_requested;
    return (
      <Card title={cancelRequested ? "Cancelling scoring…" : "Scoring in progress"}>
        <div className="space-y-3 py-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", fontWeight: 500 }}>
                {liveCompleted} / {liveTotal} responses
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--alpha-teal)", fontWeight: 600 }}>
                {livePct.toFixed(1)}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--muted)" }}>
              <div
                className="h-full transition-all duration-200"
                style={{
                  width: `${livePct}%`,
                  backgroundColor: cancelRequested ? "var(--chart-3)" : "var(--alpha-teal)",
                }}
              />
            </div>
          </div>

          <div style={pMuted}>
            {selectedModel?.label ?? "The judge model"} is scoring every
            response for {customer.brand_name}. Safe to leave this tab —
            progress survives a reload.
          </div>

          {awaitingBatch && !cancelRequested && (
            <div style={{ ...pMuted, color: "var(--muted-foreground)" }}>
              Waiting on judge batch{" "}
              <code style={{ fontSize: "var(--text-xs)" }}>{awaitingBatch.batchId}</code>
              {awaitingBatch.total > 0 && (
                <> — {awaitingBatch.finished} of {awaitingBatch.total} requests
                  had finished at the provider when this pass handed the run
                  back</>
              )}
              . Batches take minutes to hours; this one is outlasting a single
              worker pass, so scoring picks it back up automatically. The batch
              is not resubmitted and is not billed twice.
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Running total (token-priced):{" "}
              <strong style={{ color: "var(--foreground)" }}>
                ${(liveCost ?? 0).toFixed(2)}
              </strong>
              <span style={{ marginLeft: 6, opacity: 0.75 }}>
                — batched requests are added when their batch is collected. The
                final measured cost is shown when the pass completes.
              </span>
            </span>
            <button
              onClick={handleCancel}
              disabled={cancelling || cancelRequested}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md"
              style={{
                backgroundColor: "transparent",
                color: "var(--infrared-coral)",
                border: "1px solid var(--infrared-coral)",
                fontSize: "var(--text-xs)",
                fontWeight: 500,
                cursor: cancelling || cancelRequested ? "not-allowed" : "pointer",
                opacity: cancelling || cancelRequested ? 0.6 : 1,
              }}
            >
              <StopCircle className="w-3.5 h-3.5" />
              {cancelRequested ? "Cancelling…" : "Cancel scoring"}
            </button>
          </div>
        </div>
      </Card>
    );
  }

  const hasResults = !!summary;
  // A completed stage5 exists when we have a finalized summary or the latest
  // scoring run is in 'completed' state.
  const scoringCompleted = hasResults || scoringRun?.status === "completed";
  const dl = (k: string) => downloading === k;

  const passes = state?.passes ?? [];
  const activePass = state?.active_pass ?? null;
  // Re-judging with the SAME judge + rubric replaces that pass's rows rather
  // than adding a second pass (score_run clears the exact tuple first), so the
  // warning has to say something different in that case.
  const sameJudgeAsActive =
    !!activePass && !!selectedModel &&
    activePass.scorer_model === selectedModel.model_string;
  // Has this run been judged at all? The pass rows are the authority; the
  // stored summary covers runs scored before passes were recorded.
  const hasAnyPass = passes.length > 0 || hasResults;
  const stageAdvanced = (customer.current_stage ?? 0) >= 6;
  // The result card can only offer "use this pass and continue" when there IS
  // a recorded pass to point at. A run scored before pass rows existed and not
  // yet backfilled has a summary but no pass, so it keeps the original adopt
  // button — losing its only way forward is how a scored run ends up looking
  // like it needs re-scoring.
  const needsLegacyAdopt = !stageAdvanced && !activePass && hasResults;
  // The re-score confirmation gates PAYING for a pass, not adopting one that
  // already exists, so it must not disable the free adopt.
  const runScoringDisabled =
    !scorerId || enqueueing ||
    (scoringCompleted && !allowReScore && !needsLegacyAdopt);

  // The auto-pipeline is scoring RIGHT NOW. There is nothing to configure and
  // nothing to click: a judge was already chosen at Stage 4 and is running.
  if (autoPassRunning) {
    const scored = state?.counts.total ?? 0;
    const expected = state?.expected_rows ?? 0;
    const pct = expected > 0 ? Math.min(100, (scored / expected) * 100) : 0;
    return (
      <Card title="Scoring in progress">
        <div className="space-y-3 py-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", fontWeight: 500 }}>
                {scored} / {expected} responses
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--alpha-teal)", fontWeight: 600 }}>
                {pct.toFixed(1)}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--muted)" }}>
              <div
                className="h-full transition-all duration-200"
                style={{ width: `${pct}%`, backgroundColor: "var(--alpha-teal)" }}
              />
            </div>
          </div>
          <div style={pMuted}>
            This run is being scored automatically with the judge configured at
            Stage&nbsp;4
            {state?.configured_scorer_model_id
              ? ` (${state.configured_scorer_model_id})`
              : " (the default judge)"}
            . There is nothing to start here — the pass is already paid for and
            running. This panel updates itself; leaving and coming back is safe.
          </div>
          <div className="flex items-center gap-2">
            <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--muted-foreground)" }} />
            <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
              Checking every 5 seconds.
            </span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── The pass that ran ────────────────────────────────────────────
          Stage 5 is where you READ the scoring pass, not where the judge is
          chosen: that decision belongs to Stage 4, before any money is spent.
          When a pass exists this replaces the judge picker entirely, and a
          second paid pass is reachable only through the explicit, cost-labelled
          "Re-score with a different judge" below. */}
      {hasAnyPass && (
        <ScoringResultCard
          summary={summary}
          passes={passes}
          activePass={activePass}
          activeCounts={state?.active_counts ?? null}
          expectedRows={state?.expected_rows ?? 0}
          // Stage 5 is not finished until the operator accepts a pass. The
          // auto-pipeline scores the run, but adopting its pass is still a
          // human decision — so a scored run sitting at Stage 5 needs a way
          // forward that is not "run scoring again".
          stageAdvanced={stageAdvanced}
          activating={activating}
          onActivate={async (pass) => {
            if (runId == null) return;
            setActivating(pass.scorer_model);
            try {
              const res = await activateScoringPass(customer.id, runId, {
                scorer_model: pass.scorer_model,
                rubric_version: pass.rubric_version,
              });
              setSummary(res.scoring_summary);
              if (res.customer) onCustomerUpdate(res.customer as CustomerDetail);
              await refreshState();
              toast.success(
                `Reports now read the ${pass.scorer_model} pass. No re-judge, no cost.`,
              );
              // Accepting a pass completes Stage 5; the backend advances the
              // stage, so tell the stepper too.
              onStageComplete();
            } catch (e: any) {
              toast.error(e?.message || "Could not switch the active pass");
            } finally {
              setActivating(null);
            }
          }}
        />
      )}

      {/* Configuration */}
      {(!hasAnyPass || showRejudge || needsLegacyAdopt) && (
      <Card
        title={hasAnyPass ? "Re-score with a different judge" : "Configure scoring"}
        subtitle={
          hasAnyPass
            ? "A full second judge pass over all responses, at full cost. The existing pass is kept — you can switch back to it at any time for free."
            : "Pick the judge model and rubric version. The scorer registry pins per-version API constraints automatically and exposes every production judge."
        }
      >
        <div className="text-xs uppercase mb-2" style={labelStyle}>
          Scorer (judge) model
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-1">
          {models.map((m) => {
            const on = m.id === scorerId;
            return (
              <button
                key={m.id}
                onClick={() => {
                  scorerIsPreselectionRef.current = false;
                  setScorerId(m.id);
                }}
                className="flex items-start gap-3 p-3 rounded-lg text-left transition-all"
                style={{
                  backgroundColor: on ? "var(--alpha-teal-10)" : "var(--muted)",
                  border: `1px solid ${on ? "var(--alpha-teal-25)" : "var(--border)"}`,
                  cursor: "pointer",
                }}
              >
                <span
                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    backgroundColor: on ? "var(--alpha-teal)" : "transparent",
                    border: `1.5px solid ${on ? "var(--alpha-teal)" : "var(--muted-foreground)"}`,
                  }}
                >
                  {on && <Check className="w-2.5 h-2.5" strokeWidth={3} style={{ color: "white" }} />}
                </span>
                <span className="min-w-0">
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                    {m.label}
                    {m.is_default && <span style={badge("var(--alpha-teal)")}>default</span>}
                    {m.recommended && !m.is_default && (
                      <span style={badge("var(--muted-foreground)")}>recommended</span>
                    )}
                  </span>
                  <span style={{ display: "block", fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 2 }}>
                    {m.model_string} · {m.provider}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {constraintNote && (
          <div
            className="flex items-center gap-2 mt-2 mb-3 px-3 py-2 rounded-lg"
            style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
          >
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--signal-blue)" }} />
            <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>
              API parameter pinning: {constraintNote}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <div className="text-xs uppercase mb-1.5" style={labelStyle}>
              Rubric version
            </div>
            <div
              className="w-full flex items-center gap-2"
              style={{
                ...inputStyle,
                backgroundColor: "var(--muted)",
                color: "var(--muted-foreground)",
                cursor: "not-allowed",
                userSelect: "all",
              }}
              aria-readonly="true"
              title="Generated automatically from the approved framework"
            >
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--signal-blue)" }} />
              <span style={{ fontFamily: "monospace", color: "var(--foreground)" }}>
                {rubricVersion || state?.suggested_rubric_version || "pending framework approval"}
              </span>
              <span style={badge("var(--muted-foreground)")}>auto</span>
            </div>
            <p style={{ fontSize: 11, color: "var(--muted-foreground)", margin: "6px 0 0 0", lineHeight: 1.5 }}>
              <strong>What this is:</strong> a fingerprint of the approved
              scoring framework (its reasoning variables and their 0/1/2
              rubrics). The system generates it in the background and stamps it
              on every score so each result is permanently traceable to the
              exact rubric that produced it. If the framework changes and is
              re-approved, a new version is generated and used automatically.
              It is read-only by design — a free-typed value would detach
              scores from any real rubric and break the audit trail.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase mb-1.5" style={labelStyle}>Concurrency</div>
            <select
              value={concurrency}
              onChange={(e) => setConcurrency(Number(e.target.value))}
              className="w-full"
              style={inputStyle}
            >
              {[1, 3, 5, 8, 12].map((n) => (
                <option key={n} value={n}>
                  {n} parallel scorer call{n === 1 ? "" : "s"}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
              Higher = faster, more rate-limit pressure.
            </span>
          </div>
        </div>

        {state && !hasAnyPass && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
            {/* active_counts, not counts: on a twice-judged run the run-wide
                total is 2,500 rows for 1,250 responses — a number that
                describes neither pass. */}
            <Stat label="Stored scores" value={String(state.active_counts.total)} />
            <Stat label="Mention-positive" value={String(state.active_counts.mention_positive)} />
            <Stat
              label="Fell back to 0"
              value={String(state.active_counts.failed_zeroed)}
              warn={state.active_counts.failed_zeroed > 0}
            />
            <Stat
              label="Excluded (no answer)"
              value={String(state.active_counts.excluded_no_answer ?? 0)}
              warn={(state.active_counts.excluded_no_answer ?? 0) > 0}
            />
            <Stat label="Review sessions" value={String(state.review_sessions.length)} />
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 rounded-lg flex items-start gap-2"
            style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid var(--infrared-coral)" }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--infrared-coral)" }} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--infrared-coral)" }}>{error}</span>
          </div>
        )}

        {/* Failed / cancelled banner — surfaces the prior worker outcome and
            offers a one-click re-run. Scores already persisted are wiped by
            score_run() when the same scorer/rubric is selected, so a re-run
            is a clean restart by default. */}
        {scoringRun && (scoringRun.status === "failed" || scoringRun.status === "cancelled") && (
          <div
            className="mt-3 p-3 rounded-lg flex items-start gap-2"
            style={{
              backgroundColor: scoringRun.status === "failed"
                ? "rgba(220,38,38,0.08)"
                : "rgba(245,158,11,0.08)",
              border: `1px solid ${scoringRun.status === "failed" ? "var(--infrared-coral)" : "var(--chart-3)"}`,
            }}
          >
            <AlertTriangle
              className="w-4 h-4 flex-shrink-0 mt-0.5"
              style={{ color: scoringRun.status === "failed" ? "var(--infrared-coral)" : "var(--chart-3)" }}
            />
            <div className="flex-1">
              <div style={{
                fontSize: "var(--text-sm)", fontWeight: 600,
                color: scoringRun.status === "failed" ? "var(--infrared-coral)" : "var(--chart-3)",
              }}>
                {scoringRun.status === "failed" ? "Previous scoring run failed" : "Previous scoring run cancelled"}
              </div>
              {scoringRun.error_message && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", marginTop: 4 }}>
                  {scoringRun.error_message}
                </div>
              )}
              <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 4 }}>
                Stopped at {scoringRun.progress_completed} / {scoringRun.progress_total} responses.
                Click <strong>Re-run scoring</strong> to start a fresh pass (partial scores from this
                attempt will be replaced).
              </div>
            </div>
          </div>
        )}

        {/* How the pass is SENT. Batch is the default because Stage-5 scoring
            is offline work nobody waits on, and the discount is 50% of the
            largest line on the Anthropic bill. Direct is here for the case
            where a turnaround genuinely matters — labelled with what it costs,
            so choosing it is a decision rather than a surprise on the invoice.
            Same judge, same rubric, same repeats either way. */}
        <div className="mt-4">
          <span
            style={{
              display: "block",
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "var(--muted-foreground)",
              marginBottom: 6,
            }}
          >
            Scoring speed
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {([
              {
                key: undefined as ScoringMode | undefined,
                label: "Batch (default)",
                blurb: "half price · results usually within the hour",
              },
              {
                key: "direct" as ScoringMode | undefined,
                label: "Direct",
                blurb: `about ${directCostMultiplier}x the cost · scores in minutes`,
              },
            ]).map((option) => {
              const active = scoringMode === option.key;
              return (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => setScoringMode(option.key)}
                  className="text-left rounded-lg px-3 py-2 flex-1"
                  style={{
                    minWidth: 200,
                    border: `1px solid ${active ? "var(--alpha-teal)" : "var(--border)"}`,
                    backgroundColor: active ? "rgba(20,184,166,0.08)" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      fontSize: "var(--text-xs)",
                      fontWeight: 600,
                      color: "var(--foreground)",
                    }}
                  >
                    {option.label}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      color: "var(--muted-foreground)",
                      marginTop: 2,
                    }}
                  >
                    {option.blurb}
                  </span>
                </button>
              );
            })}
          </div>
          <span
            style={{
              display: "block",
              fontSize: 10,
              color: "var(--muted-foreground)",
              marginTop: 6,
              lineHeight: 1.45,
            }}
          >
            This changes only how the judge calls are sent — the judge, the
            rubric and the number of responses are identical either way.
          </span>
        </div>

        {/* Change 4: a completed stage5 locks the Run button. The data-integrity
            rule is that stage4/stage5 are immutable once scored — corrections
            mean a NEW measurement run. An admin can still opt into a fresh
            scoring attempt (recorded as a new stage5 in MANIFEST). */}
        {(scoringCompleted || hasAnyPass) && (
          <label
            className="flex items-start gap-2 mt-4 p-3 rounded-lg"
            style={{
              backgroundColor: "rgba(245,158,11,0.08)",
              border: "1px solid var(--chart-3)",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={allowReScore}
              onChange={(e) => setAllowReScore(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 11, color: "var(--foreground)", lineHeight: 1.5 }}>
              <strong>This starts a second complete judge pass and costs money.</strong>{" "}
              All {state?.expected_rows ?? "—"} responses are sent to{" "}
              {selectedModel?.label ?? "the selected judge"} again
              {sameJudgeAsActive
                ? " — and because it is the same judge and rubric as the pass you already have, that pass's rows are REPLACED."
                : " — the existing pass is kept, and you can switch back to it for free at any time."}{" "}
              Scoring is otherwise locked for this run: for data corrections,
              start a new measurement run instead. Logged as a new stage5 in the
              chain of custody.
            </span>
          </label>
        )}

        <div className="flex items-center justify-between gap-3 pt-4 mt-4 flex-wrap" style={{ borderTop: "1px solid var(--border)" }}>
          <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            {hasAnyPass
              ? `A full pass over all ${state?.expected_rows ?? "—"} responses, billed at full rate.`
              : "Scores persist with evidence — nothing is overwritten elsewhere."}
          </span>
          <div className="flex items-center gap-2">
            {hasAnyPass && (
              <button
                onClick={() => { setShowRejudge(false); setAllowReScore(false); }}
                className="px-3 py-2 rounded-lg"
                style={{
                  backgroundColor: "transparent",
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-xs)", fontWeight: 600,
                  border: "1px solid var(--border)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            )}
            <button
              onClick={run}
              disabled={runScoringDisabled}
              className="px-5 py-2.5 rounded-lg flex items-center gap-2 transition-all"
              style={{
                backgroundColor: runScoringDisabled ? "var(--muted)" : "var(--alpha-teal)",
                color: runScoringDisabled ? "var(--muted-foreground)" : "white",
                fontSize: "var(--text-sm)", fontWeight: 600,
                border: "none",
                cursor: runScoringDisabled ? "not-allowed" : "pointer",
                opacity: enqueueing ? 0.7 : 1,
              }}
            >
              {enqueueing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Gauge className="w-4 h-4" />}
              {enqueueing
                ? "Enqueueing…"
                : scoringRun && (scoringRun.status === "failed" || scoringRun.status === "cancelled")
                  ? "Re-run scoring"
                  : hasAnyPass
                    ? `Pay for a second pass on ${selectedModel?.label ?? "this judge"}`
                    : "Run scoring"}
            </button>
          </div>
        </div>
      </Card>
      )}

      {/* The ONLY route from a scored run to a second paid pass. Collapsed by
          default: opening it is the deliberate act, and the form behind it
          states the cost before the button appears. */}
      {hasAnyPass && !showRejudge && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowRejudge(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
            style={{
              backgroundColor: "transparent",
              color: "var(--muted-foreground)",
              fontSize: "var(--text-xs)", fontWeight: 600,
              border: "1px solid var(--border)",
              cursor: "pointer",
            }}
          >
            <Gauge className="w-3.5 h-3.5" />
            Re-score with a different judge…
          </button>
        </div>
      )}

      {/* Logs & data exports — Stage 4 raw responses + Stage 5 score detail */}
      <Card
        title="Logs & data exports"
        subtitle="Download the exact queries executed, the raw model responses, and the full per-response scoring detail stored in the backend."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-4 h-4" style={{ color: "var(--signal-blue)" }} />
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                Stage 4 — query &amp; response log
              </span>
            </div>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "0 0 10px 0" }}>
              Every query executed and the raw AI response stored for this run.
            </p>
            <div className="flex gap-2">
              <DownloadBtn
                busy={dl("s4csv")}
                label="CSV"
                onClick={() => guardedDownload("s4csv", () =>
                  downloadRunResponses(customer.id, runId, "csv"))}
              />
              <DownloadBtn
                busy={dl("s4json")}
                label="JSON"
                onClick={() => guardedDownload("s4json", () =>
                  downloadRunResponses(customer.id, runId, "json"))}
              />
            </div>
          </div>

          <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4" style={{ color: "var(--signal-blue)" }} />
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                Stage 5 — scoring detail
              </span>
            </div>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "0 0 10px 0" }}>
              Per-response scores, 0/1/2 per variable, evidence, scorer model
              &amp; rubric version, and any zeroed rows.
            </p>
            <div className="flex gap-2">
              <DownloadBtn
                busy={dl("s5csv")}
                label="CSV"
                disabled={!hasResults}
                onClick={() => guardedDownload("s5csv", () =>
                  downloadScoringExport(
                    customer.id, runId, "csv",
                    // Judge AND rubric: the export is ONE pass, not every row
                    // the run holds.
                    summary?.scorer_model, summary?.rubric_version))}
              />
              <DownloadBtn
                busy={dl("s5json")}
                label="JSON"
                disabled={!hasResults}
                onClick={() => guardedDownload("s5json", () =>
                  downloadScoringExport(
                    customer.id, runId, "json",
                    summary?.scorer_model, summary?.rubric_version))}
              />
            </div>
            {!hasResults && (
              <span style={{ fontSize: 11, color: "var(--muted-foreground)", display: "block", marginTop: 6 }}>
                Run scoring first to enable the scoring export.
              </span>
            )}
          </div>

          {/* Cleanroom export — tamper-evident ZIP for client / investor delivery.
              Bundles byte-identical Stage-4 + Stage-5 JSON plus HASHES.json
              (SHA-256 per file) and SUMMARY.txt. Disabled until scoring is
              complete since the hash needs a finalized scoring summary. */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4" style={{ color: "var(--signal-blue)" }} />
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                Cleanroom export
              </span>
            </div>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "0 0 10px 0" }}>
              SHA-256 verified package for client and investor delivery.
            </p>
            <div className="flex gap-2">
              <DownloadBtn
                busy={dl("cleanroom")}
                label="Download ZIP"
                disabled={!hasResults}
                onClick={() => guardedDownload("cleanroom", () =>
                  downloadCleanroomExport(runId))}
              />
            </div>
            {!hasResults && (
              <span style={{ fontSize: 11, color: "var(--muted-foreground)", display: "block", marginTop: 6 }}>
                Scoring not yet complete.
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Pre-deployment two-arm validation — gate before content deployment.
          Splits the run's scores into two arms and reports per-variable score
          stability with a PASS / CONDITIONAL / FAIL verdict. When present, the
          rendered report ships as predeployment_report.txt in the cleanroom ZIP. */}
      {hasResults && (
        <Card
          title="Pre-deployment validation"
          subtitle="Two-arm stability gate over the scored responses. Produces a PASS / CONDITIONAL / FAIL verdict with per-variable deltas, recorded in the chain of custody and bundled into the cleanroom export."
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {predeploy ? (
                <span style={badge(
                  predeploy.verdict === "PASS" ? "var(--alpha-teal)"
                    : predeploy.verdict === "CONDITIONAL" ? "var(--chart-3)"
                    : "var(--infrared-coral)",
                )}>
                  {predeploy.verdict}
                </span>
              ) : (
                <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  Not yet run.
                </span>
              )}
              {predeploy && (
                <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                  arm A: {predeploy.arm_a_n} · arm B: {predeploy.arm_b_n}
                  {predeploy.fail_vars.length > 0 && ` · fail: ${predeploy.fail_vars.join(", ")}`}
                  {predeploy.warn_vars.length > 0 && ` · warn: ${predeploy.warn_vars.join(", ")}`}
                </span>
              )}
            </div>
            <button
              onClick={runValidation}
              disabled={predeployBusy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: predeployBusy ? "var(--muted)" : "var(--alpha-teal)",
                color: predeployBusy ? "var(--muted-foreground)" : "white",
                fontSize: "var(--text-xs)", fontWeight: 600, border: "none",
                cursor: predeployBusy ? "not-allowed" : "pointer",
              }}
            >
              {predeployBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {predeploy ? "Re-run validation" : "Run validation"}
            </button>
          </div>

          {predeploy && Object.keys(predeploy.per_variable || {}).length > 0 && (
            <div className="space-y-1.5">
              {Object.entries(predeploy.per_variable)
                .sort((a, b) => (b[1].delta ?? 0) - (a[1].delta ?? 0))
                .map(([rv, info]) => {
                  const color = info.status === "FAIL" ? "var(--infrared-coral)"
                    : info.status === "WARN" ? "var(--chart-3)" : "var(--alpha-teal)";
                  return (
                    <div key={rv} className="flex items-center gap-3" style={{ fontSize: "var(--text-xs)" }}>
                      <span style={{ width: 220, flexShrink: 0, color: "var(--muted-foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={rv}>
                        {rv}
                      </span>
                      <span style={{ width: 60, textAlign: "right" }}>{info.arm_a?.toFixed(2) ?? "—"}</span>
                      <span style={{ width: 60, textAlign: "right" }}>{info.arm_b?.toFixed(2) ?? "—"}</span>
                      <span style={{ width: 60, textAlign: "right", fontWeight: 600 }}>Δ {info.delta?.toFixed(2) ?? "—"}</span>
                      <span style={badge(color)}>{info.status}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </Card>
      )}

      {/* Results */}
      {hasResults && summary && (
        <Card title="Scoring result">
          <div className="flex flex-wrap items-center gap-2 mb-3" style={{ fontSize: 11 }}>
            <span style={badge("var(--signal-blue)")}>{summary.scorer_model}</span>
            <span style={badge("var(--muted-foreground)")}>rubric {summary.rubric_version}</span>
            <span style={badge("var(--muted-foreground)")}>
              {summary.responses_scored} scored
            </span>
            {summary.failed_zeroed > 0 && (
              <span style={badge("var(--chart-3)")}>
                {summary.failed_zeroed} zeroed (failed)
              </span>
            )}
            {(summary.excluded_no_answer ?? 0) > 0 && (
              <span
                style={badge("var(--chart-3)")}
                title="The measurement call returned no answer. Kept for the audit trail; outside every rate."
              >
                {summary.excluded_no_answer} excluded (no answer)
              </span>
            )}
          </div>

          {/* Judge cost — the token-measured number the provider actually
              billed, and ONLY that.
              On summaries written before 2026-10-30, scoring_cost_usd_est is
              calls x a flat per-call constant that did not depend on the judge
              ($75.00 on both an $11.39 Sonnet pass and a $22.55 Opus pass), so
              it is never shown as a cost. Newer summaries set it equal to the
              measured figure, or null for an unpriced judge. */}
          <div
            className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-4 p-3 rounded-lg"
            style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
          >
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontWeight: 500 }}>
              Judge cost
            </span>
            {measuredScoringCost(summary) != null ? (
              <>
                <span style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--foreground)" }}>
                  ${(measuredScoringCost(summary) as number).toFixed(2)}
                </span>
                <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                  token-measured
                </span>
                {summary.token_usage && summary.token_usage.cache_hit_rate > 0 && (
                  <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                    · {(summary.token_usage.cache_hit_rate * 100).toFixed(0)}% cache hit
                  </span>
                )}
                {/* Say when the discount applied. The measured cost above is
                    already the discounted figure, so without this the number
                    just looks unaccountably low next to an older pass. */}
                {summary.token_usage?.batch_discount_applied && (
                  <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                    · batch API, half price
                  </span>
                )}
                {summary.token_usage && (
                  <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                    · {summary.token_usage.input_tokens.toLocaleString()} in /{" "}
                    {summary.token_usage.output_tokens.toLocaleString()} out tokens
                  </span>
                )}
              </>
            ) : (
              <>
                <span style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--muted-foreground)" }}>
                  not measured
                </span>
                <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                  no token-measured price for this judge
                </span>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <Stat label="Mention rate" value={fmtPct(summary.mention_rate)} />
            <Stat label="Top-5 rate" value={fmtPct(summary.top5_rate)} />
            <Stat label="Primary rec" value={fmtPct(summary.primary_rec_rate)} />
            <Stat label="#1 rate" value={fmtPct(summary.number_one_rate)} />
          </div>

          {runId != null && (
            <div className="mb-2">
              <RubricResults
                customerId={customer.id}
                runId={runId}
                reasoningVariables={customer.framework?.reasoning_variables}
                rubricVersion={summary.rubric_version}
                scorerModel={summary.scorer_model}
                runParams={[
                  { label: "Judge", value: summary.scorer_model },
                  { label: "Rubric", value: summary.rubric_version },
                  { label: "Responses", value: String(summary.responses_scored) },
                  ...(Array.isArray(customer.framework?.queries)
                    ? [{ label: "Queries", value: String(customer.framework!.queries.length) }]
                    : []),
                ]}
                title="Rubric results — per variable, distribution &amp; evidence"
              />
            </div>
          )}

          <div className="flex items-center justify-between pt-3 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Next: human review &amp; Cohen's weighted Kappa (Stage 6).
            </span>
            <div className="flex items-center gap-2">
              {onOpenReview && (
                <button
                  onClick={() => onOpenReview(runId)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: "var(--alpha-teal)", color: "white", fontSize: "var(--text-sm)", fontWeight: 600, border: "none", cursor: "pointer" }}
                >
                  Go to human review <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Local UI helpers ────────────────────────────────────────────────────────

function DownloadBtn({
  label, busy, disabled, onClick,
}: {
  label: string; busy: boolean; disabled?: boolean; onClick: () => void;
}) {
  const off = busy || disabled;
  return (
    <button
      onClick={onClick}
      disabled={off}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
      style={{
        background: "transparent",
        color: off ? "var(--muted-foreground)" : "var(--signal-blue)",
        fontSize: "var(--text-xs)", fontWeight: 600,
        border: `1px solid ${off ? "var(--border)" : "var(--signal-blue)"}`,
        cursor: off ? "not-allowed" : "pointer",
      }}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

/**
 * What Stage 5 shows once a run has been judged: the pass that ran.
 *
 * It replaces the judge picker entirely. The judge was chosen at Stage 4 and
 * the pipeline has already scored with it, so re-asking the question here is
 * how run 29 got a second $11.39 pass on a run that was already complete.
 *
 * Every pass a run has ever had is listed, and switching which one the reports
 * read is a pointer change — free, reversible, nothing deleted.
 */
function ScoringResultCard({
  summary,
  passes,
  activePass,
  activeCounts,
  expectedRows,
  stageAdvanced,
  activating,
  onActivate,
}: {
  summary: ScoringSummary | null;
  passes: ScoringPass[];
  activePass: ScoringPass | null;
  activeCounts: ScoringCounts | null;
  expectedRows: number;
  stageAdvanced: boolean;
  activating: string | null;
  onActivate: (pass: ScoringPass) => void;
}) {
  const measured = measuredScoringCost(summary);
  const rowsScored =
    activeCounts?.total ?? activePass?.responses_scored ?? summary?.responses_scored ?? null;
  const failedZeroed =
    activeCounts?.failed_zeroed ?? activePass?.failed_zeroed ?? summary?.failed_zeroed ?? 0;
  // Rows whose measurement call returned no answer: stored, outside every
  // rate. Shown so "1,000 rows, rates on 975" reads as explained.
  const excludedNoAnswer =
    activeCounts?.excluded_no_answer ?? activePass?.excluded_no_answer ??
    summary?.excluded_no_answer ?? 0;
  const incomplete = expectedRows > 0 && rowsScored != null && rowsScored < expectedRows;
  const rebuilt =
    (activePass?.summary_origin ?? summary?.summary_origin) === "rebuilt";

  return (
    <Card
      title="Scoring pass"
      subtitle="The judge was chosen at Stage 4 and this run has already been scored with it. Nothing on this card starts a judge pass."
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat
          label="Judge"
          value={activePass?.scorer_model ?? summary?.scorer_model ?? "—"}
        />
        <Stat
          label="Responses scored"
          value={
            rowsScored == null
              ? "—"
              : expectedRows
                ? `${rowsScored} / ${expectedRows}`
                : String(rowsScored)
          }
          warn={incomplete}
        />
        <Stat
          label="Fell back to 0"
          value={String(failedZeroed)}
          warn={failedZeroed > 0}
        />
        <Stat
          label="Excluded (no answer)"
          value={String(excludedNoAnswer)}
          warn={excludedNoAnswer > 0}
        />
        {/* MEASURED spend, never the flat per-call placeholder. That placeholder
            read $75.00 on both an $11.39 Sonnet pass and a $22.55 Opus pass,
            because it is calls x a constant and does not depend on the judge. */}
        <Stat
          label="Cost (measured)"
          value={measured != null ? `$${measured.toFixed(2)}` : "not measured"}
        />
      </div>

      <div
        className="mt-3 px-3 py-2 rounded-lg flex flex-wrap items-center gap-x-4 gap-y-1"
        style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
      >
        <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
          Rubric{" "}
          <strong style={{ fontFamily: "monospace", color: "var(--foreground)" }}>
            {activePass?.rubric_version ?? summary?.rubric_version ?? "—"}
          </strong>
        </span>
        {(activePass?.scored_at ?? summary?.scored_at) && (
          <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
            Scored{" "}
            <strong style={{ color: "var(--foreground)" }}>
              {new Date(activePass?.scored_at ?? (summary?.scored_at as string)).toLocaleString()}
            </strong>
          </span>
        )}
        {summary?.parse_failures ? (
          <span style={{ fontSize: 11, color: "var(--chart-3)" }}>
            {summary.parse_failures} repl{summary.parse_failures === 1 ? "y" : "ies"} never
            parsed, after retries
          </span>
        ) : null}
      </div>

      {rebuilt && (
        <div
          className="mt-3 p-3 rounded-lg flex items-start gap-2"
          style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
        >
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--signal-blue)" }} />
          <span style={{ fontSize: 11, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
            These numbers were re-aggregated from this pass's stored score rows —
            same rows, same aggregation, same stage-5 hash as the original. Token
            usage is recorded per API call rather than per row, so this pass's
            spend cannot be restated; the scores themselves are unaffected.
          </span>
        </div>
      )}

      {incomplete && (
        <div
          className="mt-3 p-3 rounded-lg flex items-start gap-2"
          style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid var(--chart-3)" }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--chart-3)" }} />
          <span style={{ fontSize: 11, color: "var(--foreground)", lineHeight: 1.5 }}>
            This pass covers {rowsScored} of {expectedRows} responses. A partial
            pass cannot be adopted — the run needs a complete pass before the
            report and blueprint are built on it.
          </span>
        </div>
      )}

      {/* Two or more passes on one run: the operator chooses which one the
          reports and Deep Signal read. Switching is free. */}
      {passes.length > 1 && (
        <div className="mt-4">
          <div className="text-xs uppercase mb-2" style={labelStyle}>
            Scoring passes on this run
          </div>
          <div className="space-y-2">
            {passes.map((p) => {
              const busy = activating === p.scorer_model;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg flex-wrap"
                  style={{
                    backgroundColor: p.is_active ? "var(--alpha-teal-10)" : "var(--muted)",
                    border: `1px solid ${p.is_active ? "var(--alpha-teal-25)" : "var(--border)"}`,
                  }}
                >
                  <div className="min-w-0">
                    <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                      <Scale className="w-3.5 h-3.5 inline mr-1.5" style={{ color: "var(--signal-blue)" }} />
                      {p.scorer_model}
                      {p.is_active && <span style={badge("var(--alpha-teal)")}>active</span>}
                      {p.summary_origin === "rebuilt" && (
                        <span style={badge("var(--muted-foreground)")}>rebuilt</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 2 }}>
                      {p.responses_scored ?? "—"} rows ·{" "}
                      {p.cost_usd_measured != null
                        ? `$${p.cost_usd_measured.toFixed(2)} measured`
                        : "cost not measured"}{" "}
                      {/* The transport, beside the cost it explains: two
                          passes of the same size on the same judge differ by
                          2x on this alone. null reads as "not recorded" and
                          never as the default. */}
                      · {p.scoring_mode ?? "transport not recorded"}{" "}
                      · rubric <span style={{ fontFamily: "monospace" }}>{p.rubric_version}</span>
                    </div>
                  </div>
                  {p.is_active ? (
                    <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                      Reports read this pass
                    </span>
                  ) : (
                    <button
                      onClick={() => onActivate(p)}
                      disabled={!!activating}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md"
                      style={{
                        backgroundColor: "transparent",
                        color: "var(--signal-blue)",
                        fontSize: "var(--text-xs)", fontWeight: 600,
                        border: "1px solid var(--signal-blue)",
                        cursor: activating ? "not-allowed" : "pointer",
                        opacity: activating && !busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Use this pass (free)
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: "var(--muted-foreground)", margin: "8px 0 0 0", lineHeight: 1.5 }}>
            Switching passes changes which one the report, the blueprint and Deep
            Signal read. No judge is called and no scores are deleted, so you can
            switch back at any time.
          </p>
        </div>
      )}

      {/* The run is scored but Stage 5 is not finished until a human accepts
          the pass. Without this, a run the auto-pipeline had already judged
          left the operator on a finished-looking screen whose only button was
          "re-score" — the exact dead end that makes paying twice look like the
          way forward. */}
      {!stageAdvanced && activePass && !incomplete && (
        <div
          className="flex items-center justify-between gap-3 pt-4 mt-4 flex-wrap"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Accepting this pass completes Stage 5. Nothing is judged and nothing
            is charged.
          </span>
          <button
            onClick={() => onActivate(activePass)}
            disabled={!!activating}
            className="px-5 py-2.5 rounded-lg flex items-center gap-2"
            style={{
              backgroundColor: activating ? "var(--muted)" : "var(--alpha-teal)",
              color: activating ? "var(--muted-foreground)" : "white",
              fontSize: "var(--text-sm)", fontWeight: 600,
              border: "none",
              cursor: activating ? "not-allowed" : "pointer",
            }}
          >
            {activating
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Check className="w-4 h-4" />}
            Use this pass and continue
            {!activating && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      )}
    </Card>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
      <div className="mb-4">
        <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>{title}</h3>
        {subtitle && (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "2px 0 0 0" }}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: `1px solid ${warn ? "var(--chart-3)" : "var(--border)"}` }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 2, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: warn ? "var(--chart-3)" : "var(--foreground)" }}>{value}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)",
  borderRadius: 8, outline: "none", fontFamily: "inherit",
};
const labelStyle: React.CSSProperties = {
  color: "var(--muted-foreground)", letterSpacing: "0.05em", fontWeight: 600,
};
const pMuted: React.CSSProperties = {
  fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
};
const badge = (c: string): React.CSSProperties => ({
  padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
  color: c, backgroundColor: "var(--muted)", border: `1px solid ${c}`,
  marginLeft: 6,
});

function fmtPct(v: any): string {
  if (v == null || isNaN(Number(v))) return "—";
  return `${(Number(v) * 100).toFixed(1)}%`;
}